/**
 * Gender Party Broadcast Screen
 * Fullscreen display for projector/TV showing voting in real-time.
 * Background orbs shift toward the leading vote side.
 * Vote emojis float up as people vote in real-time.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGenderPartySocket,
  type GenderVote,
  type GenderPartyVoteCounts,
} from "./hooks";
import { PhotoCarousel, VotingBar, RevealAnimation } from "./components";
import { VoteBurst } from "./components/VoteBurst";
import { useAuth } from "@/lib/auth";
import styles from "./GenderPartyScreen.module.css";

export function GenderPartyScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { account } = useAuth();

  const [showRevealAnimation, setShowRevealAnimation] = useState(false);
  const [revealedAnswer, setRevealedAnswer] = useState<GenderVote | null>(null);

  // Vote burst tracking
  const prevVoteCountsRef = useRef<GenderPartyVoteCounts | null>(null);
  const [burstTrigger, setBurstTrigger] = useState(0);
  const [boyDelta, setBoyDelta] = useState(0);
  const [girlDelta, setGirlDelta] = useState(0);

  // Bouncing emoji above voting bar
  const [bouncingEmoji, setBouncingEmoji] = useState<"boy" | "girl" | null>(null);
  const bounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleAnswer = useCallback(
    (answer: GenderVote, _voteCounts: GenderPartyVoteCounts) => {
      setRevealedAnswer(answer);
      setShowRevealAnimation(true);
    },
    []
  );

  const {
    isConnected,
    isJoined,
    error,
    genderParty,
    photos,
    voteCounts,
    isCompleted,
  } = useGenderPartySocket({
    genderPartyId: id ?? "",
    accountId: account?.id,
    onAnswer: handleAnswer,
  });

  // Detect new votes → trigger floating emoji burst + bouncing emoji
  useEffect(() => {
    if (!voteCounts) return;
    const prev = prevVoteCountsRef.current;

    if (prev) {
      const newBoy = Math.max(0, voteCounts.boyVotes - prev.boyVotes);
      const newGirl = Math.max(0, voteCounts.girlVotes - prev.girlVotes);

      if (newBoy > 0 || newGirl > 0) {
        setBoyDelta(newBoy);
        setGirlDelta(newGirl);
        setBurstTrigger((t) => t + 1);

        // Show bouncing emoji (prefer the one with more new votes)
        if (bounceTimeoutRef.current) {
          clearTimeout(bounceTimeoutRef.current);
        }
        setBouncingEmoji(newBoy >= newGirl ? "boy" : "girl");
        bounceTimeoutRef.current = setTimeout(() => {
          setBouncingEmoji(null);
        }, 1500);
      }
    }

    prevVoteCountsRef.current = voteCounts;
  }, [voteCounts]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (bounceTimeoutRef.current) {
        clearTimeout(bounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (genderParty?.status === "COMPLETED" && genderParty.answer && !showRevealAnimation) {
      setRevealedAnswer(genderParty.answer);
    }
  }, [genderParty?.status, genderParty?.answer, showRevealAnimation]);

  useEffect(() => {
    document.documentElement.setAttribute("data-gender-party-page", "true");
    return () => document.documentElement.removeAttribute("data-gender-party-page");
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Escape") navigate(-1);
    };
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [navigate]);

  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    const requestWakeLock = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch {
        // Wake lock not supported — not critical
      }
    };
    requestWakeLock();
    return () => { wakeLock?.release(); };
  }, []);

  if (!id) {
    return (
      <div className={styles.errorScreen}>
        <div className={styles.errorText}>Gender Party не найден</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorScreen}>
        <div className={styles.errorText}>{error}</div>
        <button className={styles.retryButton} onClick={() => window.location.reload()}>
          Повторить
        </button>
      </div>
    );
  }

  if (!isConnected || !isJoined || !genderParty) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.spinner} />
        <div className={styles.loadingText}>Подключение...</div>
      </div>
    );
  }

  if (showRevealAnimation && revealedAnswer) {
    return (
      <RevealAnimation
        answer={revealedAnswer}
        onComplete={() => setShowRevealAnimation(false)}
      />
    );
  }

  const boyPct = voteCounts?.boyPercentage ?? 50;
  const girlPct = voteCounts?.girlPercentage ?? 50;

  // Orb opacity: 0.25 baseline + up to 0.55 at 100%
  const boyOrbOpacity = 0.25 + (boyPct / 100) * 0.55;
  const girlOrbOpacity = 0.25 + (girlPct / 100) * 0.55;
  // Orb size: grows with percentage
  const boyOrbSize = 50 + (boyPct / 100) * 30;
  const girlOrbSize = 50 + (girlPct / 100) * 30;

  const showResults =
    genderParty.showResultsMode === "always" ||
    genderParty.status === "REVEALED" ||
    genderParty.status === "COMPLETED";

  return (
    <div
      className={styles.screen}
      style={{
        "--boy-orb-opacity": boyOrbOpacity,
        "--girl-orb-opacity": girlOrbOpacity,
        "--boy-orb-size": `${boyOrbSize}vmin`,
        "--girl-orb-size": `${girlOrbSize}vmin`,
      } as React.CSSProperties}
    >
      {/* Animated background orbs */}
      <div className={styles.bgOrbs} aria-hidden="true">
        <div className={styles.boyOrb} />
        <div className={styles.girlOrb} />
      </div>

      {/* Floating vote emojis */}
      <VoteBurst
        boyDelta={boyDelta}
        girlDelta={girlDelta}
        trigger={burstTrigger}
        size="large"
      />

      <header className={styles.header}>
        <h1 className={styles.title}>{genderParty.name}</h1>
        <div className={styles.coupleInfo}>
          <span className={`${styles.partnerName} ${styles.partnerBoy}`}>
            {genderParty.partnerOneName}
          </span>
          <span className={styles.and}>&</span>
          <span className={`${styles.partnerName} ${styles.partnerGirl}`}>
            {genderParty.partnerTwoName}
          </span>
        </div>
      </header>

      <div className={styles.content}>
        <PhotoCarousel
          photos={photos}
          couplePhotoUrl={genderParty.couplePhotoUrl}
          autoPlay={true}
          autoPlayInterval={6000}
          className={styles.carousel}
        />

        <div className={styles.votingSection}>
          {bouncingEmoji && (
            <div
              className={`${styles.bouncingEmoji} ${
                bouncingEmoji === "boy" ? styles.bouncingBoy : styles.bouncingGirl
              }`}
            >
              {bouncingEmoji === "boy" ? "👦" : "👧"}
            </div>
          )}
          <VotingBar
            voteCounts={voteCounts}
            showResults={showResults}
            revealedAnswer={isCompleted ? revealedAnswer : null}
            animate={true}
            size="large"
          />
        </div>
      </div>

      {genderParty.isTest && (
        <div className={styles.testBadge}>ТЕСТ</div>
      )}
    </div>
  );
}
